import type { JsonObject } from 'type-fest';

import {
  prepareAndMakeRequest,
  type ChatAnnotation,
  type ChatAnnotationViewerContext,
  type ChatChannel,
  type ChatChannelType,
  type ChatComment,
  type ChatCommentContentType,
  type ChatShare,
  type ChatSupplierMscSettings,
  type ChatTask,
  type ChatTaskStatus,
  type PartViewerPageData,
  type LegacyFileTaskStatusData,
  type LegacyFileTaskStatus,
  type TechnicalDataPackage,
  type SharedTechnicalDataPackage,
  type CustomInterrogationListItem,
  type InterrogationRequestBody,
  type ListCustomInterrogationsParams,
} from '@frontend/api';

/** Integer primary key; DRF's IntegerField also accepts numeric strings */
type EntityId = number | string;

/** 204 No Content (and other empty bodies) come back as `''` from `getPayload` */
type EmptyResponseBody = '';

type TaskInput = { assignedToId: EntityId; dueDate?: string };

/** AnnotationListCreateView.Validator */
export type AnnotationData = Partial<ChatAnnotationViewerContext> & {
  initialComment: CommentContentData[];
  partMasterVersion: EntityId;
  actionUuid?: string | null;
  commentedOnQuoteItemId?: EntityId | null;
  featureIds?: number[] | null;
  feedbackItemIds?: number[] | null;
  partFile?: string | null;
  partInterrogationFileId?: EntityId | null;
  /** Not read by the backend */
  resolved?: boolean;
  task?: TaskInput | null;
};

type SupportingFileAccessData = {
  accessGranted?: boolean;
  partFileUuid: string;
  /** Not read by the backend */
  filename?: string;
};

/** ChannelDetailsView.Validator */
type ChannelPatchData = {
  accessToAllPartFiles?: boolean;
  accessToPrimaryFile?: boolean;
  name?: string | null;
  permissionToDownloadFiles?: boolean;
  permissionToUploadFiles?: boolean;
  supportingFilesAccess?: SupportingFileAccessData[] | null;
};

/** ChannelListCreateView.Validator */
type ChannelCreateData = ChannelPatchData & {
  message?: string | null;
  partMasterVersion: EntityId;
  type: ChatChannelType;
};

/** CommentListCreateView / CommentDetailsView validate_content_dict */
type CommentContentData = {
  type: ChatCommentContentType;
  content: string | null;
  /** Required for mentions, must be `null` otherwise */
  userId?: EntityId | null;
};

/** ShareCreateView.Validator */
type ShareCreateData = {
  emailAddress: string;
  emailSubjectLine?: string | null;
  expirationDate?: string | null;
  message?: string | null;
  partMasterVersion: EntityId;
};

/** ShareDetailsView.Validator */
type SharePatchData = {
  expirationDate?: string | null;
};

/**
 * ShareSerializer with no instance. ShareDetailsView.perform_update never
 * returns the share and ShareCreateView returns none for same-group invites, so
 * only the initial value of the one writable field comes back.
 */
type EmptyShareResponse = { emailSubjectLine: '' };

/** SharedWithMeListSerializer */
type SharedWithMeListItem = {
  accessRevokedOn: string | null;
  accessToPrimaryFile: boolean;
  channelId: number;
  id: number;
  isExpired: boolean;
  /** PartMasterVersionSerializer */
  part: JsonObject;
  sharedByBusinessName: string | null;
  sharedByEmail: string;
  sharedByFullName: string | null;
  unviewedCommentsCount: number;
};

/** TaskDetailsView.Validator */
type TaskPatchData = {
  assignedToId?: EntityId;
  dueDate?: string;
  status?: ChatTaskStatus;
};

export type LegacyFileTaskStatusResponse = {
  statuses: {
    interrogation: LegacyFileTaskStatusData & { uuid: string };
    processing: {
      uuid: string;
      geometricIndexing: LegacyFileTaskStatusData;
      processing: LegacyFileTaskStatusData;
      thumbnailCreation: LegacyFileTaskStatusData;
      upload: LegacyFileTaskStatusData;
      vectorization: LegacyFileTaskStatusData;
      viewerCreation: LegacyFileTaskStatusData;
    }[];
  };
};

type PartUploadResponse = {
  uuid: string;
  partMasterUuid: string;
  version: string;
  primaryFile: {
    filename: string;
    status: LegacyFileTaskStatus;
    uuid: string;
  };
};

const annotations = {
  create: (
    channelId: EntityId,
    data: AnnotationData,
  ): Promise<ChatAnnotation> =>
    prepareAndMakeRequest.post<ChatAnnotation>(
      `/part_library/channels/${channelId}/annotations`,
      { body: data },
    ),

  delete: (annotationId: EntityId): Promise<EmptyResponseBody> =>
    prepareAndMakeRequest.delete<EmptyResponseBody>(
      `/part_library/annotations/${annotationId}`,
    ),

  /** No matching route in part_library/urls.py */
  stream: (
    partUUID: string,
    { lastMessageReceived }: { lastMessageReceived: string },
  ): Promise<unknown> =>
    prepareAndMakeRequest.get(`/part_library/${partUUID}/stream`, {
      query: { lastMessageReceived },
    }),
};

const channels = {
  create: (partUUID: string, data: ChannelCreateData): Promise<ChatChannel> =>
    prepareAndMakeRequest.post<ChatChannel>(
      `/part_library/${partUUID}/channels`,
      { body: data },
    ),

  get: (channelId: EntityId): Promise<ChatChannel> =>
    prepareAndMakeRequest.get<ChatChannel>(
      `/part_library/channels/${channelId}`,
    ),

  list: (partUUID: string): Promise<ChatChannel[]> =>
    prepareAndMakeRequest.get<ChatChannel[]>(
      `/part_library/${partUUID}/channels`,
    ),

  patch: (channelId: EntityId, data: ChannelPatchData): Promise<ChatChannel> =>
    prepareAndMakeRequest.patch<ChatChannel>(
      `/part_library/channels/${channelId}`,
      { body: data },
    ),
};

const comments = {
  create: (
    annotationId: EntityId,
    data: { content: CommentContentData[]; parentId: EntityId | null },
  ): Promise<ChatComment> =>
    prepareAndMakeRequest.post<ChatComment>(
      `/part_library/annotations/${annotationId}/comments`,
      { body: data },
    ),

  delete: (commentId: EntityId): Promise<EmptyResponseBody> =>
    prepareAndMakeRequest.delete<EmptyResponseBody>(
      `/part_library/comments/${commentId}`,
    ),

  patch: (
    commentId: EntityId,
    data: { content: CommentContentData[] },
  ): Promise<ChatComment> =>
    prepareAndMakeRequest.patch<ChatComment>(
      `/part_library/comments/${commentId}`,
      { body: data },
    ),
};

const mscChannelConfig = {
  patch: async (
    channelId: EntityId,
    data: { mscSpecialistId?: EntityId | null },
  ): Promise<ChatChannel> =>
    prepareAndMakeRequest.patch<ChatChannel>(
      `/part_library/channels/${channelId}/msc_channel_config`,
      { body: data },
    ),
};

const sharedTechDataPackage = {
  get: (
    uuid: string,
    queryParams?: { version?: string },
  ): Promise<SharedTechnicalDataPackage> =>
    prepareAndMakeRequest.get<SharedTechnicalDataPackage>(
      `/part_library/shared_technical_data_package/${uuid}`,
      { query: queryParams },
    ),

  requestAccess: (
    uuid: string,
    data: { version: EntityId },
  ): Promise<EmptyResponseBody> =>
    prepareAndMakeRequest.post<EmptyResponseBody>(
      `/part_library/shared_technical_data_package/${uuid}/request_access`,
      { body: data },
    ),
};

const shares = {
  create: (
    channelId: EntityId,
    data: ShareCreateData,
  ): Promise<ChatShare | EmptyShareResponse> =>
    prepareAndMakeRequest.post<ChatShare | EmptyShareResponse>(
      `/part_library/channels/${channelId}/shares`,
      { body: data },
    ),

  delete: (shareId: EntityId): Promise<EmptyResponseBody> =>
    prepareAndMakeRequest.delete<EmptyResponseBody>(
      `/part_library/shares/${shareId}`,
    ),

  patch: (
    shareId: EntityId,
    data: SharePatchData,
  ): Promise<EmptyShareResponse> =>
    prepareAndMakeRequest.patch<EmptyShareResponse>(
      `/part_library/shares/${shareId}`,
      { body: data },
    ),
};

const suppliersMscSettings = {
  get: (): Promise<ChatSupplierMscSettings> =>
    prepareAndMakeRequest.get<ChatSupplierMscSettings>(
      `/part_library/msc/settings`,
    ),
};

const tasks = {
  patch: (taskId: EntityId, data: TaskPatchData): Promise<ChatTask> =>
    prepareAndMakeRequest.patch<ChatTask>(`/part_library/tasks/${taskId}`, {
      body: data,
    }),
};

export default {
  annotations,
  channels,
  comments,
  mscChannelConfig,
  sharedTechDataPackage,
  shares,
  suppliersMscSettings,
  tasks,

  checkStatuses: (uuids: string[]): Promise<LegacyFileTaskStatus[]> =>
    prepareAndMakeRequest.get('/part_library/statuses', { query: { uuids } }),

  get: (uuid: string): Promise<PartViewerPageData> =>
    prepareAndMakeRequest.get<PartViewerPageData>(`/part_library/${uuid}`),

  list: async ({
    page,
    search,
  }: {
    page?: number;
    search?: string | null;
  }): Promise<{ results: PartViewerPageData[] }> =>
    prepareAndMakeRequest.get<{ results: PartViewerPageData[] }>(
      '/part_library/',
      {
        query: { ...(page && { page }), search: search ?? '' },
      },
    ),

  listSharedParts: ({
    page,
    search,
  }: {
    page?: number;
    search?: string | null;
  }): Promise<{ results: SharedWithMeListItem[] }> =>
    prepareAndMakeRequest.get<{ results: SharedWithMeListItem[] }>(
      '/part_library/shared_with_me/',
      { query: { ...(page && { page }), search: search ?? '' } },
    ),

  listArchivedParts: ({
    page,
    search,
  }: {
    page?: number;
    search?: string | null;
  }): Promise<{ results: PartViewerPageData; count: number }> =>
    prepareAndMakeRequest.get<{ results: PartViewerPageData; count: number }>(
      '/part_library/archived_parts/',
      { query: { ...(page && { page }), search: search ?? '' } },
    ),

  partUpload: (formData: FormData): Promise<PartUploadResponse> =>
    prepareAndMakeRequest.post<PartUploadResponse>(
      '/part_library/file_upload',
      { body: formData },
    ),

  getTechnicalDataPackage: (
    uuid: string,
    queryParams?: { version?: string; quoteItemId?: string },
  ): Promise<TechnicalDataPackage> =>
    prepareAndMakeRequest.get<TechnicalDataPackage>(
      `/part_library/technical_data_package/${uuid}`,
      { query: queryParams },
    ),

  supportingFileUpload: ({
    formData,
    uuid,
  }: {
    formData: FormData;
    uuid: string;
  }): Promise<PartViewerPageData> =>
    prepareAndMakeRequest.post<PartViewerPageData>(
      `/part_library/${uuid}/files`,
      {
        body: formData,
      },
    ),

  listCustomInterrogations: ({
    uuid,
    params,
  }: {
    uuid: string;
    params: ListCustomInterrogationsParams;
  }): Promise<CustomInterrogationListItem[]> =>
    prepareAndMakeRequest.get<CustomInterrogationListItem[]>(
      `/part_library/${uuid}/custom_interrogations`,
      { query: params },
    ),

  interrogationRequest: ({
    uuid,
    data,
  }: {
    uuid: string;
    data: InterrogationRequestBody;
  }): Promise<Record<string, LegacyFileTaskStatusData>> =>
    prepareAndMakeRequest.post<Record<string, LegacyFileTaskStatusData>>(
      `/part_library/${uuid}/interrogation_request`,
      { body: data },
    ),
};
